import { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    Users, Plus, Edit3, Copy, Trash2, Eye, EyeOff, Loader2, CheckCircle2,
    AlertTriangle, BadgeCheck, GripVertical, X, Save, FileText, Upload, Crown, Power,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import TeamMemberAvatar from "../TeamMemberAvatar";

/**
 * Admin → Team Management
 *
 * Unified single-panel admin for the Founder + every team member.
 *  ✓ Add / Edit / Delete / Duplicate
 *  ✓ Change name, designation (role), bio, responsibilities, education, quote
 *  ✓ Upload / replace profile photo (via existing site-assets pipeline)
 *  ✓ Drag-and-drop reorder (@dnd-kit/sortable)
 *  ✓ Enable / disable without deleting (soft-hide on /team)
 *  ✓ Save as draft (is_published=false) or publish instantly
 *  ✓ Preview changes before publishing (renders card preview in editor)
 *  ✓ All updates reflect instantly — public API returns published+enabled
 *    without any caching, so toggles are immediate.
 */
export default function TeamManagementPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [flash, setFlash] = useState("");
    const [error, setError] = useState("");
    const [editor, setEditor] = useState(null);
    const [busy, setBusy] = useState("");
    const [photoBust, setPhotoBust] = useState(0);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const refresh = async () => {
        const { data } = await api.get("/admin/team-members");
        setItems(data.items || []);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/team-members");
                if (cancelled) return;
                setItems(data.items || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const showFlash = (m) => { setFlash(m); setTimeout(() => setFlash(""), 3500); };
    const showError = (e) => { setError(e); setTimeout(() => setError(""), 5000); };

    const handleDragEnd = async (e) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = items.findIndex((i) => i.id === active.id);
        const newIdx = items.findIndex((i) => i.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const reordered = arrayMove(items, oldIdx, newIdx);
        // Optimistic update with new display_order values
        const updated = reordered.map((m, idx) => ({ ...m, display_order: idx }));
        setItems(updated);
        try {
            await api.post("/admin/team-members/reorder", {
                items: updated.map((m) => ({ id: m.id, display_order: m.display_order })),
            });
            showFlash("Order saved. Updated on the live website.");
        } catch (err) {
            showError(err?.response?.data?.detail || "Reorder failed");
            await refresh();
        }
    };

    const onToggleEnabled = async (m) => {
        setBusy(`toggle:${m.id}`);
        try {
            await api.post(`/admin/team-members/${m.id}/toggle-enabled`);
            await refresh();
            showFlash(m.is_enabled ? `Hidden "${m.name}" from the website.` : `Showing "${m.name}" on the website.`);
        } catch (err) {
            showError(err?.response?.data?.detail || "Toggle failed");
        } finally { setBusy(""); }
    };

    const onPublishToggle = async (m) => {
        setBusy(`pub:${m.id}`);
        try {
            await api.post(`/admin/team-members/${m.id}/${m.is_published ? "unpublish" : "publish"}`);
            await refresh();
            showFlash(m.is_published ? `"${m.name}" reverted to Draft.` : `"${m.name}" published live.`);
        } catch (err) {
            showError(err?.response?.data?.detail || "Publish toggle failed");
        } finally { setBusy(""); }
    };

    const onDuplicate = async (m) => {
        setBusy(`dup:${m.id}`);
        try {
            await api.post(`/admin/team-members/${m.id}/duplicate`);
            await refresh();
            showFlash(`Duplicated "${m.name}" as a draft. Edit and publish when ready.`);
        } catch (err) {
            showError(err?.response?.data?.detail || "Duplicate failed");
        } finally { setBusy(""); }
    };

    const onDelete = async (m) => {
        if (!window.confirm(`Delete "${m.name}"?\nThis also removes the linked photo asset.`)) return;
        setBusy(`del:${m.id}`);
        try {
            await api.delete(`/admin/team-members/${m.id}`);
            await refresh();
            showFlash(`Deleted "${m.name}".`);
        } catch (err) {
            showError(err?.response?.data?.detail || "Delete failed");
        } finally { setBusy(""); }
    };

    if (loading) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center" data-testid="team-mgmt-loading">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="team-management-panel">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A] inline-flex items-center gap-2">
                        <Users className="w-6 h-6 text-[#1E3A8A]" /> Team Management
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Founder + team profiles on <code className="bg-slate-100 px-1 rounded">/founder</code> &amp; <code className="bg-slate-100 px-1 rounded">/team</code>. Drag to reorder · toggle Show/Hide · publish drafts · all changes go live instantly.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setEditor({ mode: "create" })}
                    data-testid="team-mgmt-create"
                    className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white px-4 py-2 text-sm font-semibold rounded-lg hover:bg-blue-800"
                >
                    <Plus className="w-4 h-4" /> Add Team Member
                </button>
            </div>

            {flash && (
                <div data-testid="team-mgmt-flash" className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> {flash}
                </div>
            )}
            {error && (
                <div data-testid="team-mgmt-error" className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                </div>
            )}

            {/* Sortable list */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3" data-testid="team-mgmt-list">
                        {items.map((m) => (
                            <SortableRow
                                key={m.id}
                                member={m}
                                busy={busy}
                                photoBust={photoBust}
                                onEdit={() => setEditor({ mode: "edit", member: m })}
                                onToggleEnabled={() => onToggleEnabled(m)}
                                onPublishToggle={() => onPublishToggle(m)}
                                onDuplicate={() => onDuplicate(m)}
                                onDelete={() => onDelete(m)}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            {editor && (
                <MemberEditor
                    mode={editor.mode}
                    member={editor.member}
                    onClose={() => setEditor(null)}
                    onPhotoUploaded={(name) => {
                        setPhotoBust((n) => n + 1);
                        showFlash(`Photo updated for ${name}. Live on /team now.`);
                        toast.success(`Profile photo updated — ${name}`, { description: "Live on /team and /founder." });
                    }}
                    onPhotoError={(msg) => toast.error(msg || "Photo upload failed")}
                    onSaved={async (savedName, statusVerb) => {
                        setEditor(null);
                        await refresh();
                        showFlash(`${savedName} ${statusVerb}.`);
                        toast.success(`${savedName} ${statusVerb}`);
                    }}
                />
            )}
        </div>
    );
}


/* ============================ Sortable Row ============================ */

function SortableRow({ member, busy, photoBust, onEdit, onToggleEnabled, onPublishToggle, onDuplicate, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: member.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <article
            ref={setNodeRef}
            style={style}
            data-testid={`team-mgmt-row-${member.id}`}
            className={`bg-white border-2 rounded-2xl p-4 flex items-center gap-4 transition-shadow ${
                isDragging ? "shadow-2xl border-blue-400" : member.is_founder ? "border-amber-300" : "border-slate-200 hover:border-blue-200"
            }`}
        >
            {/* Drag handle */}
            <button
                type="button"
                {...attributes}
                {...listeners}
                aria-label="Drag to reorder"
                data-testid={`team-mgmt-drag-${member.id}`}
                className="p-2 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 shrink-0"
            >
                <GripVertical className="w-5 h-5" />
            </button>

            {/* Avatar */}
            <div className="shrink-0">
                <TeamMemberAvatar
                    assetKey={member.photo_asset_key}
                    bustKey={photoBust}
                    size="w-14 h-14"
                    alt={member.name}
                    ringClass="ring-2 ring-white shadow"
                    fallback={<span className="text-lg font-bold text-slate-500">{(member.name || "?").charAt(0)}</span>}
                />
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-[#0F172A] truncate">{member.name}</h3>
                    {member.is_founder && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                            <Crown className="w-3 h-3" /> Founder
                        </span>
                    )}
                    {!member.is_published && (
                        <span data-testid={`team-mgmt-draft-badge-${member.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 rounded">
                            <FileText className="w-3 h-3" /> Draft
                        </span>
                    )}
                    {!member.is_enabled && (
                        <span data-testid={`team-mgmt-hidden-badge-${member.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                    )}
                    {member.is_published && member.is_enabled && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                            <BadgeCheck className="w-3 h-3" /> Live
                        </span>
                    )}
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{member.role}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">Order #{member.display_order} · Updated {(member.updated_at || "").slice(0, 10)}</div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
                <IconBtn onClick={onEdit} testid={`team-mgmt-edit-${member.id}`} title="Edit" tone="blue"><Edit3 className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={onPublishToggle} disabled={busy === `pub:${member.id}`} testid={`team-mgmt-publish-${member.id}`} title={member.is_published ? "Unpublish (draft)" : "Publish"} tone="emerald">
                    {busy === `pub:${member.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : (member.is_published ? <FileText className="w-4 h-4" /> : <BadgeCheck className="w-4 h-4" />)}
                </IconBtn>
                <IconBtn onClick={onToggleEnabled} disabled={busy === `toggle:${member.id}`} testid={`team-mgmt-toggle-${member.id}`} title={member.is_enabled ? "Hide on site" : "Show on site"} tone="amber">
                    {busy === `toggle:${member.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : (member.is_enabled ? <Power className="w-4 h-4" /> : <Eye className="w-4 h-4" />)}
                </IconBtn>
                <IconBtn onClick={onDuplicate} disabled={busy === `dup:${member.id}`} testid={`team-mgmt-duplicate-${member.id}`} title="Duplicate" tone="slate">
                    {busy === `dup:${member.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                </IconBtn>
                <IconBtn onClick={onDelete} disabled={busy === `del:${member.id}`} testid={`team-mgmt-delete-${member.id}`} title="Delete" tone="red">
                    {busy === `del:${member.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </IconBtn>
            </div>
        </article>
    );
}

function IconBtn({ children, testid, title, tone = "slate", onClick, disabled }) {
    const tones = {
        blue: "bg-blue-50 hover:bg-blue-100 text-blue-700",
        emerald: "bg-emerald-50 hover:bg-emerald-100 text-emerald-700",
        amber: "bg-amber-50 hover:bg-amber-100 text-amber-700",
        slate: "bg-slate-100 hover:bg-slate-200 text-slate-700",
        red: "bg-red-50 hover:bg-red-100 text-red-700",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            title={title}
            aria-label={title}
            className={`p-2 rounded-md ${tones[tone]} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            {children}
        </button>
    );
}


/* =========================== Editor Modal ============================ */

const EMPTY_DRAFT = {
    name: "",
    role: "",
    bio: "",
    quote: "",
    responsibilities: [],
    education: [],
    achievements: [],
    is_founder: false,
    is_published: true,
    is_enabled: true,
};

function MemberEditor({ mode, member, onClose, onSaved, onPhotoUploaded, onPhotoError }) {
    const isNew = mode === "create";
    const [form, setForm] = useState(() => isNew ? { ...EMPTY_DRAFT } : { ...EMPTY_DRAFT, ...member });
    const [respText, setRespText] = useState(((member?.responsibilities) || []).join("\n"));
    const [eduText, setEduText] = useState(((member?.education) || []).join("\n"));
    const [achText, setAchText] = useState(((member?.achievements) || []).map((a) => `${a.year}|${a.text}`).join("\n"));
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("edit"); // 'edit' | 'preview'
    const [photoBusy, setPhotoBusy] = useState(false);
    const [photoTick, setPhotoTick] = useState(0); // bump to force avatar refresh after upload
    const [photoSuccess, setPhotoSuccess] = useState(""); // inline confirmation shown inside the modal

    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", h); document.body.style.overflow = ""; };
    }, [onClose]);

    const photoKey = useMemo(() => {
        if (member?.photo_asset_key) return member.photo_asset_key;
        // For new members: derive from slug-ish name (real slug assigned server-side after save)
        const slug = (form.name || "new-member").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
        return slug ? (form.is_founder ? "founder_photo" : `team_photo_${slug}`) : "";
    }, [member, form.name, form.is_founder]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const parseResp = () => respText.split("\n").map((s) => s.trim()).filter(Boolean);
    const parseEdu = () => eduText.split("\n").map((s) => s.trim()).filter(Boolean);
    const parseAch = () => achText.split("\n").map((s) => s.trim()).filter(Boolean).map((line) => {
        const [year, ...rest] = line.split("|");
        return { year: (year || "").trim(), text: rest.join("|").trim() };
    }).filter((a) => a.year && a.text);

    const buildPayload = (extra = {}) => ({
        name: form.name.trim(),
        role: form.role.trim(),
        bio: form.bio.trim(),
        quote: (form.quote || "").trim() || null,
        responsibilities: parseResp(),
        education: parseEdu(),
        achievements: parseAch(),
        is_founder: !!form.is_founder,
        is_published: !!form.is_published,
        is_enabled: !!form.is_enabled,
        ...extra,
    });

    const save = async (publishOverride) => {
        if (!form.name.trim()) { setErr("Name is required"); return; }
        if (!form.role.trim()) { setErr("Designation / role is required"); return; }
        if ((form.bio || "").trim().length < 10) { setErr("Bio is too short (min 10 chars)"); return; }
        setSaving(true);
        setErr("");
        try {
            const payload = buildPayload(publishOverride !== undefined ? { is_published: publishOverride } : {});
            if (isNew) {
                await api.post("/admin/team-members", payload);
            } else {
                await api.patch(`/admin/team-members/${member.id}`, payload);
            }
            const verb = payload.is_published ? "saved and published" : "saved as draft";
            onSaved(form.name, verb);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
        if (!allowed.includes(file.type)) {
            const msg = "Photo must be JPG, PNG, or WebP";
            setErr(msg);
            onPhotoError && onPhotoError(msg);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            const msg = "Photo exceeds 5 MB limit";
            setErr(msg);
            onPhotoError && onPhotoError(msg);
            return;
        }
        setPhotoBusy(true);
        setErr("");
        setPhotoSuccess("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const token = localStorage.getItem("at_token");
            const res = await fetch(`${API}/admin/site-assets/${encodeURIComponent(photoKey)}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `Photo upload failed (${res.status})`);
            }
            const meta = await res.json().catch(() => ({}));
            setPhotoTick((t) => t + 1);
            setPhotoSuccess(`Photo updated · version ${meta.version || "new"} · live on /team and /founder`);
            onPhotoUploaded && onPhotoUploaded(form.name || member?.name || "Team member");
        } catch (e) {
            const msg = e.message || "Photo upload failed";
            setErr(msg);
            onPhotoError && onPhotoError(msg);
        } finally {
            setPhotoBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="team-mgmt-editor">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <div>
                        <div className="text-[10px] font-bold tracking-widest text-amber-600 uppercase">{isNew ? "Add" : "Edit"} team member</div>
                        <h3 className="font-heading text-xl font-bold text-[#0F172A]">{form.name || (isNew ? "New team member" : "Editing")}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="bg-slate-100 rounded-lg p-1 flex">
                            <button type="button" onClick={() => setTab("edit")} data-testid="team-mgmt-tab-edit" className={`px-3 py-1.5 text-xs font-semibold rounded ${tab === "edit" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Edit</button>
                            <button type="button" onClick={() => setTab("preview")} data-testid="team-mgmt-tab-preview" className={`px-3 py-1.5 text-xs font-semibold rounded ${tab === "preview" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Preview</button>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Close" data-testid="team-mgmt-editor-close" className="p-2 rounded-full hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {tab === "edit" ? (
                        <div className="grid md:grid-cols-[260px_1fr] gap-6">
                            {/* Photo column */}
                            <div className="space-y-3">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Profile photo</div>
                                <div className="flex flex-col items-center bg-slate-50 border border-slate-200 rounded-xl p-4">
                                    <TeamMemberAvatar
                                        key={`${photoKey}-${photoTick}`}
                                        assetKey={photoKey}
                                        size="w-32 h-32"
                                        priority
                                        ringClass="ring-4 ring-white shadow"
                                        fallback={<span className="text-3xl font-bold text-slate-400">{(form.name || "?").charAt(0).toUpperCase()}</span>}
                                    />
                                    <label htmlFor="team-mgmt-photo" className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                                        {photoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                        {photoBusy ? "Uploading…" : "Upload / Replace"}
                                    </label>
                                    <input
                                        id="team-mgmt-photo"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        className="hidden"
                                        data-testid="team-mgmt-photo-input"
                                        disabled={photoBusy || isNew}
                                        onChange={(e) => uploadPhoto(e.target.files?.[0])}
                                    />
                                    <p className="text-[10px] text-slate-500 text-center mt-2">JPG / PNG / WebP · max 5 MB · auto-cropped to 512×512 WebP</p>
                                    {isNew && (
                                        <p className="text-[10px] text-amber-700 text-center mt-1 font-semibold">Save the member first, then upload a photo.</p>
                                    )}
                                    {photoSuccess && (
                                        <div
                                            data-testid="team-mgmt-photo-success"
                                            className="mt-3 w-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-2 rounded text-[11px] font-semibold inline-flex items-start gap-1.5"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                            <span>{photoSuccess}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2 text-sm">
                                    <Checkbox
                                        label="Mark as Founder"
                                        checked={!!form.is_founder}
                                        onChange={(v) => set({ is_founder: v })}
                                        testid="team-mgmt-is-founder"
                                        hint="Pins to top with Founder badge."
                                    />
                                    <Checkbox
                                        label="Show on website"
                                        checked={!!form.is_enabled}
                                        onChange={(v) => set({ is_enabled: v })}
                                        testid="team-mgmt-is-enabled"
                                        hint="Off = hidden from /team without deleting."
                                    />
                                    <Checkbox
                                        label="Publish (live)"
                                        checked={!!form.is_published}
                                        onChange={(v) => set({ is_published: v })}
                                        testid="team-mgmt-is-published"
                                        hint="Off = stays as Draft."
                                    />
                                </div>
                            </div>

                            {/* Fields column */}
                            <div className="space-y-4">
                                <Field label="Name *" testid="team-mgmt-name">
                                    <input type="text" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Anand Rajput" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                </Field>
                                <Field label="Designation / Role *" testid="team-mgmt-role">
                                    <input type="text" value={form.role} onChange={(e) => set({ role: e.target.value })} placeholder="e.g. Chief Operations & Technology Officer (COO)" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                </Field>
                                <Field label="Bio *" testid="team-mgmt-bio" hint="Use blank lines to split paragraphs.">
                                    <textarea value={form.bio} onChange={(e) => set({ bio: e.target.value })} rows={5} placeholder="Long-form bio paragraph(s)…" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                </Field>
                                {form.is_founder && (
                                    <Field label="Pull quote (shown on /founder)" testid="team-mgmt-quote">
                                        <textarea value={form.quote || ""} onChange={(e) => set({ quote: e.target.value })} rows={2} placeholder="A short founder quote (no quotation marks needed)" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                    </Field>
                                )}
                                <Field label="Key responsibilities" testid="team-mgmt-resp" hint="One per line. Each becomes a chip badge.">
                                    <textarea value={respText} onChange={(e) => setRespText(e.target.value)} rows={4} placeholder={"Tutor Assignment & Matching\nParent Feedback Management\nService Quality Monitoring"} className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                </Field>
                                {form.is_founder && (
                                    <>
                                        <Field label="Education / credentials (founder)" testid="team-mgmt-edu" hint="One per line. First item shown in sidebar.">
                                            <textarea value={eduText} onChange={(e) => setEduText(e.target.value)} rows={3} placeholder={"B.Tech in Mechanical Engineering\nSpecialist in CBSE / ICSE / MP-Board curriculum design"} className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                        </Field>
                                        <Field label="Milestones / timeline (founder)" testid="team-mgmt-ach" hint='Format: "YEAR|short description". One per line.'>
                                            <textarea value={achText} onChange={(e) => setAchText(e.target.value)} rows={5} placeholder={"2013|Founded Anand Tutorials with a strict 1:1 home-tuition model in Jabalpur.\n2016|Expanded to Bhopal."} className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                                        </Field>
                                    </>
                                )}
                                {err && <div data-testid="team-mgmt-editor-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {err}</div>}
                            </div>
                        </div>
                    ) : (
                        <PreviewCard form={form} respText={respText} photoKey={photoKey} photoTick={photoTick} />
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2 bg-slate-50">
                    <button type="button" onClick={onClose} disabled={saving} data-testid="team-mgmt-editor-cancel" className="px-4 py-2 text-sm rounded-lg bg-slate-200 hover:bg-slate-300 disabled:opacity-60">Cancel</button>
                    <button type="button" onClick={() => save(false)} disabled={saving} data-testid="team-mgmt-save-draft" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-60">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Save as Draft
                    </button>
                    <button type="button" onClick={() => save(true)} disabled={saving} data-testid="team-mgmt-save-publish" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save &amp; Publish
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children, testid }) {
    return (
        <div data-testid={testid}>
            <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
            {children}
            {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
        </div>
    );
}

function Checkbox({ label, checked, onChange, testid, hint }) {
    return (
        <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5 w-4 h-4 rounded text-blue-600" />
            <div>
                <div className="text-xs font-semibold text-slate-700">{label}</div>
                {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
            </div>
        </label>
    );
}


/* =========================== Preview Card ============================ */

function PreviewCard({ form, respText, photoKey, photoTick }) {
    const responsibilities = respText.split("\n").map((s) => s.trim()).filter(Boolean);
    return (
        <div className="max-w-2xl mx-auto" data-testid="team-mgmt-preview-card">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">Preview · how this card will look on /team</div>
            <div className={`bg-white border-2 rounded-2xl p-6 ${form.is_founder ? "border-amber-300 shadow-lg" : "border-slate-200"}`}>
                <div className="mb-4 flex items-center gap-4">
                    <TeamMemberAvatar
                        key={`prev-${photoKey}-${photoTick}`}
                        assetKey={photoKey}
                        size="w-16 h-16"
                        alt={form.name}
                        ringClass="ring-2 ring-white shadow"
                        fallback={<span className="text-xl font-bold text-slate-400">{(form.name || "?").charAt(0).toUpperCase()}</span>}
                    />
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{(form.role || "").split("—")[0].trim()}</div>
                        <h3 className="font-heading font-bold text-lg text-[#0F172A] leading-snug truncate">{form.name || "Name will appear here"}</h3>
                        {form.is_founder && (
                            <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                <Crown className="w-3 h-3" /> Founder
                            </span>
                        )}
                    </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 whitespace-pre-wrap">{form.bio || "Bio will appear here…"}</p>
                {responsibilities.length > 0 && (
                    <>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Key Responsibilities</div>
                        <div className="flex flex-wrap gap-1.5">
                            {responsibilities.map((e, i) => (
                                <span key={i} className="text-[10px] font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{e}</span>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap gap-3">
                <span>Status: <strong>{form.is_published ? "Published" : "Draft"}</strong></span>
                <span>Visibility: <strong>{form.is_enabled ? "Shown" : "Hidden"}</strong></span>
            </div>
        </div>
    );
}
